import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
  useContext,
  MouseEventHandler,
  Dispatch,
  SetStateAction,
  useRef,
} from 'react';

import {
  Table,
  Button,
  Menu,
  Dropdown,
  Tooltip,
  ConfigProvider,
  Alert,
  Divider,
} from 'antd';

import { ColumnHeightOutlined, SettingOutlined } from '@ant-design/icons';
import { FilterQueryItems } from '@components/QueryBuilder/types';
import { useTranslation } from 'react-i18next';
import { getAntLocale } from '@utils/locales';
import { ColumnType, TablePaginationConfig, TableProps } from 'antd/lib/table';
import QuerySelectFilters from '@components/QueryBuilder/QuerySelectFilters';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { Filters, FiltersCard, ITableFilters } from '@components/Filters';
import SessionContext from '@providers/Session/SessionContext';
import { formatBRNumber } from '@utils/formatters';
import StatusMenu, { StatusItemProps } from '@components/StatusMenu';
import GroupedStatusMenu, {
  GroupedStatusItem,
} from '@components/GroupedStatusMenu';
import CustomCard from '@components/CustomCard';
import { TableLocale } from 'antd/lib/table/interface';
import ModalSelectColumns from '../ModalSelectColumns';
import * as S from './styles';

export interface MyColumnType extends ColumnType<any> {
  visible: boolean;
}

interface StatusMenuTotalProps {
  value: number;
  visible: boolean;
  title: string;
  onClick: () => void;
}

export interface ITableStatusMenu {
  open: boolean;
  itemActive: string;
  list: StatusItemProps[];
  onChange: (item: StatusItemProps) => void;
  total?: StatusMenuTotalProps;
  showIconClear?: boolean;
}
export interface ITableGroupedStatusMenu {
  open: boolean;
  itemActive: string;
  actionActive: string;
  list: GroupedStatusItem[];
  onChange: (item: StatusItemProps | undefined) => void;
  showIconClear?: boolean;
}

export interface ITableTabs {
  activeKey: string | undefined;
  hasAll?: boolean;
  defaultActiveKey?: string;
  list: {
    label: string;
    key: string;
  }[];
  onChange: (activeKey: string) => void;
}

interface IPagination extends TablePaginationConfig {
  page?: number | string;
  linesPerPage?: number | string;
}

interface ITableProps {
  columns: ColumnType<any>[];
  data: any[];
  tableKey: string;
  loading?: boolean;
  title?: ReactNode;
  footer?: (currentPageData: any) => any;
  onRow?: (record: any, index?: number) => React.HTMLAttributes<HTMLElement>;
  selectable?: boolean;
  className?: string;
  setSelectedRowKeys?: any;
  extraActions?: ReactNode;
  filters?: {
    list: ITableFilters[];
    hasValuesInFilters: boolean;
    onClearFilters: MouseEventHandler<HTMLButtonElement>;
    openByDefault?: boolean;
  };
  alertMessage?: {
    text: string;
    style?: React.CSSProperties;
  };
  statusMenu?: ITableStatusMenu;
  groupedMenu?: ITableGroupedStatusMenu;
  tabs?: ITableTabs;
  maxHeight?: string;
  mainExtraAction?: ReactNode;
  pagination?: IPagination;
  noPagination?: boolean;
  customRowKey?: string;
  customRowKeyList?: string[];
  onTableChange?: ({ page, linesPerPage }: IPagination) => void;
  disabledPaginationWhenAll?: boolean;
  locale?: TableLocale;
  queryBuilder?: any;
  queryFilters?: {
    filters: FilterQueryItems[];
    clearSearchParams: (paramsToDelete?: string[]) => void;
    itemsToMap: FilterQueryItems[] | undefined;
    setItemsToMap: Dispatch<SetStateAction<FilterQueryItems[] | undefined>>;
    hasValuesInFilters?: boolean;
  };
  rowClassName?: any;
  customPagination?: ReactNode;
  disableTableConfig?: boolean;
  expandable?: TableProps<any>['expandable'];
}

const DEFAULT_PAGINATION: TablePaginationConfig = {
  current: 1,
  pageSize: 20,
  pageSizeOptions: [20, 40, 80, 160],
};

const ParametrizedTable: React.FC<ITableProps> = ({
  columns,
  data,
  loading,
  title,
  tableKey,
  footer,
  onRow,
  selectable,
  className,
  setSelectedRowKeys: setParentSelectedRowKeys,
  extraActions,
  filters,
  alertMessage,
  statusMenu,
  groupedMenu,
  tabs,
  pagination,
  noPagination,
  maxHeight,
  mainExtraAction,
  customRowKey,
  customRowKeyList,
  onTableChange,
  disabledPaginationWhenAll = true,
  locale,
  queryBuilder,
  queryFilters,
  rowClassName,
  customPagination,
  disableTableConfig,
  expandable,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('table');
  const {
    userPreferences: { setTablePreferences, getTablePreferences },
  } = useContext(SessionContext);

  const [modalColumnsVisible, setModalColumnsVisible] = useState(false);
  const [size, setSize] = useState<SizeType>('small');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [formattedColumns, setFormattedColumns] = useState<MyColumnType[]>();
  const [selectedFilters, setSelectedFilters] = useState<string[] | undefined>(
    queryFilters?.itemsToMap?.map(item => item.value),
  );
  const [showFilters, setShowFilters] = useState(false);
  const [isTableBottomVisible, setIsTableBottomVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableBottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tablePreferences = getTablePreferences(tableKey);
    const columnPreferences = tablePreferences?.columnPreferences;
    const tableSize = tablePreferences?.size;
    const userSelectedFilters = tablePreferences?.filterSelected;
    setSelectedFilters(userSelectedFilters);

    if (tableSize) {
      setSize(tableSize);
    }
    if (columnPreferences) {
      const sortedColumns: any = [...columns]
        .sort((a, b) => {
          const indexA = columnPreferences?.order.indexOf(`${a.dataIndex}`);
          const indexB = columnPreferences?.order.indexOf(`${b.dataIndex}`);
          return indexA - indexB;
        })
        ?.map(col => ({
          ...col,
          visible: columnPreferences?.visibility?.includes(`${col.dataIndex}`),
        }));
      setFormattedColumns(sortedColumns);
    } else {
      setFormattedColumns(
        columns?.map(col => ({
          ...col,
          visible: true,
        })),
      );
    }
  }, [columns, getTablePreferences, tableKey]);

  useEffect(() => {
    if (filters?.hasValuesInFilters || filters?.openByDefault) {
      setShowFilters(true);
    }
  }, [filters?.hasValuesInFilters, filters?.openByDefault, queryFilters]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsTableBottomVisible(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      },
    );

    const currentRef = tableBottomRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [data]);

  const displayingAllItems = useMemo(
    () => (pagination?.total ? data?.length === pagination?.total : false),
    [data?.length, pagination?.total],
  );

  const defaultPagination = useMemo(
    () =>
      !displayingAllItems || !disabledPaginationWhenAll
        ? {
            ...DEFAULT_PAGINATION,
            ...pagination,
            current: pagination?.page ? Number(pagination?.page) : 1,
            pageSize: pagination?.linesPerPage
              ? Number(pagination?.linesPerPage)
              : 20,
          }
        : false,
    [disabledPaginationWhenAll, displayingAllItems, pagination],
  );

  const handleUpdateTableSize = useCallback(
    ({ key }: any) => {
      setTablePreferences(tableKey, { size: key });
    },
    [setTablePreferences, tableKey],
  );

  const tableSizeMenu = useMemo(
    () => (
      <Menu selectedKeys={size && [size]} onClick={handleUpdateTableSize}>
        {['small', 'middle', 'large'].map(item => (
          <Menu.Item key={item}>{t(`line-heights.${item}`)}</Menu.Item>
        ))}
      </Menu>
    ),
    [handleUpdateTableSize, size, t],
  );

  const onSelectedRowsChange = useCallback(
    (newSelectedRowKeys: any, newSelectedRows: any) => {
      setParentSelectedRowKeys(newSelectedRowKeys, newSelectedRows);
      setSelectedRowKeys(newSelectedRowKeys);
    },
    [setParentSelectedRowKeys],
  );

  const tableColumnsWidth = useMemo(() => {
    let sumColumnsWidth = 0;
    formattedColumns?.forEach(object => {
      if (!object.title) {
        sumColumnsWidth += 40;
      } else {
        // @ts-ignore
        sumColumnsWidth += object.title?.length * 20;
      }
    });
    return sumColumnsWidth;
  }, [formattedColumns]);

  const handleTableChange = (
    paginationObj: TablePaginationConfig | null | undefined,
  ) => {
    if (paginationObj && onTableChange) {
      const { current, pageSize } = paginationObj;
      onTableChange({
        page: current,
        linesPerPage: pageSize,
      });
    }
  };

  const handleRowKey = useCallback(
    (record: any) => {
      let rowKey = record.id;
      if (customRowKeyList) {
        let newKey = '';
        customRowKeyList.forEach(key => {
          newKey += record[key];
        });
        rowKey = newKey;
      } else if (customRowKey) {
        rowKey = record[customRowKey];
      }
      return `${tableKey}-${rowKey}`;
    },
    [customRowKey, customRowKeyList, tableKey],
  );

  const handleOpenFilters = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  const tabsItems = useMemo(() => {
    if (tabs) {
      const defaultItems = [
        {
          label: t('all'),
          key: 'all',
        },
        ...tabs.list,
      ];
      if (!tabs?.hasAll) {
        defaultItems.shift();
      }
      return defaultItems;
    }
    return undefined;
  }, [t, tabs]);

  const handleUpdateFilters = useCallback(
    (newSelectedFilters: string[]) => {
      setTablePreferences(tableKey, { newSelectedFilters });
    },
    [setTablePreferences, tableKey],
  );

  const cardTitle = useMemo(
    () => (
      <S.CardTitle>
        <div className="card-title-left">
          <div className="card-title-left-title">{title}</div>
          <div className="card-title-left-right">
            {data?.length > 0 && pagination?.total && (
              <span className="card-title-left-total">
                {` (${data?.length} ${t('of', {
                  total: pagination?.total && formatBRNumber(pagination.total),
                })})`}
              </span>
            )}
            {(filters?.list || queryBuilder) && (
              <Filters
                isOpen={showFilters}
                onOpen={handleOpenFilters}
                onClear={
                  queryBuilder
                    ? () => queryFilters?.clearSearchParams()
                    : filters?.onClearFilters
                }
                hasValuesInFilters={
                  filters?.hasValuesInFilters ||
                  queryFilters?.hasValuesInFilters
                }
              />
            )}

            {showFilters && queryBuilder && (
              <div
                style={{
                  marginLeft: queryFilters?.hasValuesInFilters ? '0px' : '10px',
                }}
              >
                <QuerySelectFilters
                  queryFilters={queryFilters}
                  selectedFilters={
                    selectedFilters ||
                    queryFilters?.itemsToMap?.map(item => item.value)
                  }
                  handleUpdateFilters={handleUpdateFilters}
                />
              </div>
            )}
          </div>
        </div>

        <div className="card-title-right">
          {extraActions}
          {!disableTableConfig && (
            <>
              <Dropdown overlay={tableSizeMenu}>
                <Tooltip placement="top" title={t('line-height-settings')}>
                  <Button icon={<ColumnHeightOutlined />} />
                </Tooltip>
              </Dropdown>
              <Tooltip placement="top" title={t('columns-settings')}>
                <Button
                  icon={<SettingOutlined />}
                  onClick={() => setModalColumnsVisible(true)}
                />
              </Tooltip>
            </>
          )}
          {mainExtraAction}
        </div>
      </S.CardTitle>
    ),
    [
      title,
      data?.length,
      pagination?.total,
      t,
      filters?.list,
      filters?.onClearFilters,
      filters?.hasValuesInFilters,
      queryBuilder,
      showFilters,
      handleOpenFilters,
      queryFilters,
      selectedFilters,
      handleUpdateFilters,
      extraActions,
      disableTableConfig,
      tableSizeMenu,
      mainExtraAction,
    ],
  );

  const handleUpdateColumns = useCallback(
    (newColumns: any) => {
      const visibility: string[] = [];
      const order = newColumns?.map((col: any) => {
        if (col.visible) {
          visibility.push(col.dataIndex);
        }
        return col.dataIndex;
      });
      const columnPreferences = { order, visibility };
      setTablePreferences(tableKey, { columnPreferences });
    },
    [setTablePreferences, tableKey],
  );

  return (
    <S.Container
      ref={containerRef}
      className={isTableBottomVisible ? 'table-bottom-visible' : ''}
    >
      <ModalSelectColumns
        columns={formattedColumns ?? []}
        updateColumns={handleUpdateColumns}
        visible={modalColumnsVisible}
        onClose={() => setModalColumnsVisible(false)}
      />
      <CustomCard title={cardTitle}>
        {alertMessage?.text && (
          <Alert
            message={alertMessage?.text}
            style={{
              background: '#E6F4FF',
              border: 'none',
              borderRadius: '0',
              fontSize: '0.85rem',
              ...alertMessage?.style,
            }}
          />
        )}
        {filters?.list && (
          <FiltersCard isOpen={showFilters} tableFilters={filters?.list} />
        )}
        {showFilters && queryBuilder}
        {(statusMenu?.open || groupedMenu?.open) && (
          <S.StatusMenuContainer>
            {statusMenu?.open && (
              <StatusMenu
                itemActive={statusMenu.itemActive}
                data={statusMenu.list}
                handleSelectedCard={statusMenu.onChange}
                all={{
                  total: statusMenu.total?.value,
                  visible: statusMenu.total?.visible,
                  title: statusMenu.total?.title,
                  onClick: () => statusMenu.total?.onClick?.(),
                }}
                showIconClear={statusMenu?.showIconClear}
              />
            )}
            {groupedMenu?.open && (
              <>
                {statusMenu?.open && (
                  <Divider className="status-menu-divider" orientation="left">
                    Status
                  </Divider>
                )}
                <GroupedStatusMenu
                  itemActive={groupedMenu.itemActive}
                  actionActive={groupedMenu.actionActive}
                  data={groupedMenu.list}
                  onChange={groupedMenu.onChange}
                  showIconClear={groupedMenu?.showIconClear}
                />
              </>
            )}
          </S.StatusMenuContainer>
        )}
        {tabs && (
          <S.TabsContainer
            showFilters={showFilters}
            activeKey={tabs.activeKey}
            defaultActiveKey={tabs.defaultActiveKey ?? 'all'}
            onChange={tabs.onChange}
            items={tabsItems}
          />
        )}
        <ConfigProvider locale={getAntLocale(language)}>
          <Table
            key={tableKey}
            className={`${className} table--striped`}
            columns={formattedColumns?.reduce(
              (acc: MyColumnType[], e: MyColumnType) => {
                // If something else that is not a string, width attribute is necessary
                if (typeof e.title === 'string') {
                  e.width = e.width ?? `${e.title.length + 5}ch`;
                }
                if (e.visible === true) {
                  // If something else that is not a string, width attribute is necessary
                  if (e.title && typeof e.title === 'string') {
                    e.width = e.width ?? `${e.title.length + 5}ch`;
                  }
                  return [...acc, e];
                }
                return acc;
              },
              [],
            )}
            dataSource={data}
            loading={loading ?? !data}
            footer={footer}
            pagination={!noPagination ? defaultPagination : false}
            rowClassName={rowClassName}
            rowKey={handleRowKey}
            rowSelection={
              selectable
                ? {
                    selectedRowKeys,
                    onChange: onSelectedRowsChange,
                  }
                : undefined
            }
            scroll={{
              x: data?.length > 0 ? 'max-content' : tableColumnsWidth,
              y: maxHeight,
            }}
            sticky={{
              offsetHeader: -16,
              offsetScroll: -16,
              getContainer: () =>
                document.getElementById('root') as HTMLElement,
            }}
            size={size}
            tableLayout="auto"
            onChange={handleTableChange}
            onRow={onRow}
            locale={locale}
            expandable={expandable}
          />
        </ConfigProvider>
        {customPagination}
        <div
          ref={tableBottomRef}
          style={{
            height: '1px',
            width: '100%',
            position: 'absolute',
            bottom: '60px',
            pointerEvents: 'none',
            visibility: 'hidden',
          }}
        />
      </CustomCard>
    </S.Container>
  );
};

export default ParametrizedTable;
